/** Shared bits of the Setup page: labels, the edit draft, merge wording. */
import {
  WINE_WORLD_LABELS,
  type LookupAdminRow,
  type LookupUpdateInput,
  type RegionKind,
  type WineColor,
  type WineLookupKind,
  type WineWorld,
} from '@gwc/shared';

export interface PickedLookup {
  id: string;
  name: string;
  parentName?: string | null;
}

export const REGION_KIND_LABELS: Record<RegionKind, string> = {
  country: 'Country',
  region: 'Region',
  ava: 'AVA',
  appellation: 'Appellation',
  commune: 'Commune',
};

export interface LookupDraft {
  name: string;
  world: WineWorld | null;
  parent: PickedLookup | null;
  regionKind: RegionKind | null;
  color: WineColor | null;
}

export function draftFrom(kind: WineLookupKind, row: LookupAdminRow | null): LookupDraft {
  return {
    name: row?.name ?? '',
    world: kind === 'regions' ? (row?.world ?? null) : null,
    parent: row?.parentId ? { id: row.parentId, name: row.parentName ?? '' } : null,
    regionKind: (row?.regionKind as RegionKind | null) ?? null,
    color: (row?.color as WineColor | null) ?? null,
  };
}

/** Only what changed, in the PATCH shape. */
export function draftToPatch(kind: WineLookupKind, row: LookupAdminRow, d: LookupDraft): LookupUpdateInput {
  const patch: LookupUpdateInput = {};
  if (d.name.trim() && d.name.trim() !== row.name) patch.name = d.name.trim();
  const parentId = d.parent?.id ?? null;
  if (kind === 'regions') {
    if (parentId !== row.parentId) patch.parentId = parentId;
    if (d.world !== row.world) patch.world = d.world;
    if (d.regionKind !== row.regionKind) patch.regionKind = d.regionKind;
  } else if (kind === 'vineyards' || kind === 'producers') {
    if (parentId !== row.parentId) patch.regionId = parentId;
  } else if (d.color !== row.color) {
    patch.color = d.color;
  }
  return patch;
}

/** "Meursault — Côte de Beaune · Old World": enough to tell same-named entries apart. */
export function lookupContext(row: Pick<LookupAdminRow, 'parentName' | 'world'>): string {
  return [row.parentName, row.world ? WINE_WORLD_LABELS[row.world] : null].filter(Boolean).join(' · ');
}

const REPOINTED_LABELS: Record<string, [string, string]> = {
  wine_catalog: ['Cellar wine', 'Cellar wines'],
  wines: ['tasting pour', 'tasting pours'],
  producers: ['producer', 'producers'],
  vineyards: ['vineyard moved', 'vineyards moved'],
  regions: ['sub-region moved', 'sub-regions moved'],
  regions_merged: ['matching sub-region merged', 'matching sub-regions merged'],
  vineyards_merged: ['matching vineyard merged', 'matching vineyards merged'],
  wine_catalog_varietals: ['Cellar grape entry', 'Cellar grape entries'],
  wine_varietals: ['tasting grape entry', 'tasting grape entries'],
};

export function describeRepointed(repointed: Record<string, number>): string {
  const parts = Object.entries(repointed)
    .filter(([, n]) => n > 0)
    .map(([table, n]) => {
      const [one, many] = REPOINTED_LABELS[table] ?? [table, table];
      return `${n} ${n === 1 ? one : many}`;
    });
  return parts.length ? parts.join(', ') : 'Nothing else was using it';
}
